import { apiClient } from "../../lib/http/apiClient";
import { adminStorage } from "../admin/service";
import type {
  NudgePreview,
  ApprovalRequired,
  ApprovalRow,
  AuditRow,
  BadgeHolder,
  BadgeOverview,
  CreditReport,
  ReviewItem,
  GamificationConfig,
  GamificationOverview,
  GrantFilter,
  GrantRow,
  Paged,
  TakebackResult,
  WalletLotsView,
  GamificationConfigPayload,
  GamificationUserView,
  Quest,
  QuestUpdatePayload,
  UserSearchResult,
} from "./types";

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const BASE = "/api/admin/gamification";

export const gamificationService = {
  getConfig: async (): Promise<GamificationConfig> => {
    const res = await apiClient.get(`${BASE}/config`, { headers: adminHeaders() });
    return withConfigDefaults(res.data);
  },

  /** With two-admin approval on, a risky change is only requested (HTTP 202) and nothing changes yet. */
  updateConfig: async (body: GamificationConfigPayload): Promise<GamificationConfig | ApprovalRequired> => {
    const res = await apiClient.put(`${BASE}/config`, body, { headers: adminHeaders() });
    return isApprovalRequired(res.data) ? res.data : withConfigDefaults(res.data);
  },

  listQuests: async (): Promise<Quest[]> => {
    const res = await apiClient.get(`${BASE}/quests`, { headers: adminHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  },

  updateQuest: async (questId: string, body: QuestUpdatePayload): Promise<Quest | ApprovalRequired> => {
    const res = await apiClient.put(`${BASE}/quests/${encodeURIComponent(questId)}`, body, { headers: adminHeaders() });
    return res.data;
  },

  searchUsers: async (q: string, limit = 10): Promise<UserSearchResult[]> => {
    const res = await apiClient.get(`${BASE}/users`, { params: { q, limit }, headers: adminHeaders() });
    return Array.isArray(res.data) ? res.data : [];
  },

  getUser: async (userId: string): Promise<GamificationUserView> => {
    const res = await apiClient.get(`${BASE}/users/${encodeURIComponent(userId)}`, { headers: adminHeaders() });
    const d = res.data ?? {};
    return { user_name: d.user_name ?? null, user_email: d.user_email ?? null, user_phone: d.user_phone ?? null, grants: d.grants ?? [], progress: d.progress ?? null, badges: d.badges ?? d.progress?.badges ?? [], share: d.share ?? null, notifications: Array.isArray(d.notifications) ? d.notifications : [] };
  },

  getNudgePreview: async (): Promise<NudgePreview> => {
    const res = await apiClient.get(`${BASE}/nudge/preview`, { headers: adminHeaders() });
    return res.data;
  },

  getOverview: async (): Promise<GamificationOverview> => {
    const res = await apiClient.get(`${BASE}/overview`, { headers: adminHeaders() });
    const d = res.data ?? {};
    return { ...d, quests: Array.isArray(d.quests) ? d.quests : [] };
  },

  listGrants: async (f: GrantFilter, page: number, pageSize = 25): Promise<Paged<GrantRow>> => {
    const res = await apiClient.get(`${BASE}/grants`, { params: { ...cleanFilter(f), page, page_size: pageSize }, headers: adminHeaders() });
    return pagedOf<GrantRow>(res.data, page, pageSize);
  },

  exportGrants: async (f: GrantFilter): Promise<void> => downloadCsv(`${BASE}/grants/export.csv`, cleanFilter(f), "gamification-grants.csv"),

  getCreditReport: async (monthFrom: string, monthTo: string): Promise<CreditReport> => {
    const res = await apiClient.get(`${BASE}/credit-report`, { params: monthParams(monthFrom, monthTo), headers: adminHeaders() });
    return res.data;
  },

  exportCreditReport: async (monthFrom: string, monthTo: string): Promise<void> =>
    downloadCsv(`${BASE}/credit-report/export.csv`, monthParams(monthFrom, monthTo), "gamification-credit-report.csv"),

  listApprovals: async (status: string, page: number, pageSize = 25): Promise<Paged<ApprovalRow>> => {
    const res = await apiClient.get(`${BASE}/approvals`, { params: { ...(status ? { status } : {}), page, page_size: pageSize }, headers: adminHeaders() });
    return pagedOf<ApprovalRow>(res.data, page, pageSize);
  },

  approveRequest: async (id: string, note: string): Promise<{ status: string; error?: string | null; result?: Record<string, unknown> | null }> => {
    const res = await apiClient.post(`${BASE}/approvals/${encodeURIComponent(id)}/approve`, { note: note.trim() || null }, { headers: adminHeaders() });
    return res.data;
  },

  rejectRequest: async (id: string, note: string): Promise<void> => {
    await apiClient.post(`${BASE}/approvals/${encodeURIComponent(id)}/reject`, { note: note.trim() }, { headers: adminHeaders() });
  },

  withdrawRequest: async (id: string): Promise<void> => {
    await apiClient.post(`${BASE}/approvals/${encodeURIComponent(id)}/withdraw`, {}, { headers: adminHeaders() });
  },

  getBadges: async (): Promise<BadgeOverview> => {
    const res = await apiClient.get(`${BASE}/badges`, { headers: adminHeaders() });
    const d = res.data ?? {};
    return { items: Array.isArray(d.items) ? d.items : [], fast_responder: d.fast_responder ?? { max_minutes: 0, min_enquiries: 0, window_days: 0 } };
  },

  listBadgeHolders: async (badgeId: string, includeRevoked: boolean, page: number, pageSize = 25): Promise<Paged<BadgeHolder>> => {
    const res = await apiClient.get(`${BASE}/badges/${encodeURIComponent(badgeId)}/holders`, {
      params: { include_revoked: includeRevoked, page, page_size: pageSize }, headers: adminHeaders(),
    });
    return pagedOf<BadgeHolder>(res.data, page, pageSize);
  },

  awardBadge: async (badgeId: string, userId: string, reason: string): Promise<void> => {
    await apiClient.post(`${BASE}/badges/${encodeURIComponent(badgeId)}/award`, { user_id: userId.trim(), reason: reason.trim() }, { headers: adminHeaders() });
  },

  revokeBadge: async (badgeId: string, userId: string, reason: string): Promise<void> => {
    await apiClient.post(`${BASE}/badges/${encodeURIComponent(badgeId)}/revoke`, { user_id: userId.trim(), reason: reason.trim() }, { headers: adminHeaders() });
  },

  listReviewQueue: async (page: number, pageSize = 25): Promise<Paged<ReviewItem>> => {
    const res = await apiClient.get(`${BASE}/review-queue`, { params: { page, page_size: pageSize }, headers: adminHeaders() });
    return pagedOf<ReviewItem>(res.data, page, pageSize);
  },

  retryReviewItem: async (userId: string, grantKey: string, reason: string): Promise<{ status: string }> => {
    const res = await apiClient.post(`${BASE}/review-queue/retry`, { user_id: userId, grant_key: grantKey, reason: reason.trim() }, { headers: adminHeaders() });
    return res.data ?? { status: "unknown" };
  },

  dismissReviewItem: async (userId: string, grantKey: string, note: string): Promise<void> => {
    await apiClient.post(`${BASE}/review-queue/dismiss`, { user_id: userId, grant_key: grantKey, note: note.trim() }, { headers: adminHeaders() });
  },

  exportTakebacks: async (): Promise<void> => downloadCsv(`${BASE}/takebacks/export.csv`, {}, "gamification-takebacks.csv"),

  exportAudit: async (action: string): Promise<void> =>
    downloadCsv(`${BASE}/audit/export.csv`, action.trim() ? { action: action.trim() } : {}, "gamification-audit.csv"),

  listTakebacks: async (page: number, pageSize = 25): Promise<Paged<AuditRow>> => {
    const res = await apiClient.get(`${BASE}/takebacks`, { params: { page, page_size: pageSize }, headers: adminHeaders() });
    return pagedOf<AuditRow>(res.data, page, pageSize);
  },

  listAudit: async (action: string, page: number, pageSize = 25): Promise<Paged<AuditRow>> => {
    const res = await apiClient.get(`${BASE}/audit`, { params: { ...(action.trim() ? { action: action.trim() } : {}), page, page_size: pageSize }, headers: adminHeaders() });
    return pagedOf<AuditRow>(res.data, page, pageSize);
  },

  /** Per-user credit lots (admin users route). */
  getWalletLots: async (userId: string): Promise<WalletLotsView> => {
    const res = await apiClient.get(`/api/admin/users/${encodeURIComponent(userId)}/wallet-lots`, { headers: adminHeaders() });
    const d = res.data ?? {};
    return {
      user_id: d.user_id ?? userId,
      balance_paise: Number(d.balance_paise ?? 0),
      pending_spend_paise: Number(d.pending_spend_paise ?? 0),
      lots: Array.isArray(d.lots) ? d.lots : [],
    };
  },

  takebackReward: async (userId: string, grantKey: string, reason: string): Promise<TakebackResult> => {
    const res = await apiClient.post(
      `/api/admin/users/${encodeURIComponent(userId)}/wallet-takeback`,
      { grant_key: grantKey, reason },
      { headers: adminHeaders() },
    );
    return res.data ?? { status: "unknown" };
  },
};

export function isApprovalRequired(d: unknown): d is ApprovalRequired {
  return !!d && typeof d === "object" && (d as { status?: string }).status === "approval_required";
}

function monthParams(from: string, to: string): Record<string, string> {
  return { ...(from ? { month_from: from } : {}), ...(to ? { month_to: to } : {}) };
}

/** The endpoints need the admin auth header, which a plain link cannot send, so a CSV downloads as a blob. */
async function downloadCsv(path: string, params: Record<string, string>, filename: string): Promise<void> {
  const res = await apiClient.get(path, { params, headers: adminHeaders(), responseType: "blob" });
  const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function cleanFilter(f: GrantFilter): Record<string, string> {
  const out: Record<string, string> = {};
  (Object.entries(f) as [string, string | undefined][]).forEach(([k, v]) => {
    if (v && v.trim()) out[k] = v.trim();
  });
  return out;
}

function pagedOf<T>(d: Partial<Paged<T>> | null | undefined, page: number, pageSize: number): Paged<T> {
  return {
    items: Array.isArray(d?.items) ? (d!.items as T[]) : [],
    total: Number(d?.total ?? 0),
    total_capped: !!d?.total_capped,
    page: Number(d?.page ?? page),
    page_size: Number(d?.page_size ?? pageSize),
  };
}

// A response from an older server lacks the newer settings; show the server's own defaults for them.
function withConfigDefaults(d: Partial<GamificationConfig> | null | undefined): GamificationConfig {
  const c = d ?? {};
  return {
    enabled: !!c.enabled,
    user_cap_30d: c.user_cap_30d ?? null,
    monthly_budget: c.monthly_budget ?? null,
    expiry_days: c.expiry_days ?? 90,
    include_existing_users: !!c.include_existing_users,
    enabled_at: c.enabled_at ?? null,
    fast_responder_max_minutes: c.fast_responder_max_minutes ?? 60,
    fast_responder_min_enquiries: c.fast_responder_min_enquiries ?? 10,
    fast_responder_window_days: c.fast_responder_window_days ?? 30,
    business_start_hour: c.business_start_hour ?? 9,
    business_end_hour: c.business_end_hour ?? 19,
    business_days: c.business_days ?? [0, 1, 2, 3, 4, 5],
    push_per_day: c.push_per_day ?? 1,
    wallet_expiry_enabled: c.wallet_expiry_enabled ?? true,
    wallet_lots_enabled: c.wallet_lots_enabled ?? true,
    takeback_days: c.takeback_days ?? 14,
    clawback_floor_credits: c.clawback_floor_credits ?? 300,
    expiry_days_by_source: c.expiry_days_by_source ?? {},
    checklist_nudge_enabled: c.checklist_nudge_enabled ?? true,
    checklist_nudge_days: c.checklist_nudge_days ?? 7,
    checklist_first_deal_hold_days: c.checklist_first_deal_hold_days ?? 14,
    auto_takeback_deleted_deal: c.auto_takeback_deleted_deal ?? false,
    approvals_enabled: c.approvals_enabled ?? false,
    approval_quest_budget_threshold: c.approval_quest_budget_threshold ?? 100000,
    approval_takeback_threshold_credits: c.approval_takeback_threshold_credits ?? 500,
    approval_expiry_hours: c.approval_expiry_hours ?? 72,
    approval_campaign_apply_users: c.approval_campaign_apply_users ?? 100,
    approval_campaign_apply_credits: c.approval_campaign_apply_credits ?? 10000,
    intl_doc_retention_days: c.intl_doc_retention_days ?? 30,
  };
}
